/**
 * ReceitaViewDialog Component
 */

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Pencil, DollarSign, AlertCircle, Loader2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { supabase } from "@/integrations/supabase/client";
import { useState, useEffect } from "react";
import { PermissionGate } from "@/components/PermissionGate";
import type { ReceitaCompleta } from "@/types/receita";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

interface ReceitaViewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  receita: ReceitaCompleta | null;
  onEdit: () => void;
}

const dificuldadeLabels = {
  facil: "Fácil",
  media: "Média",
  dificil: "Difícil",
};

const tipoIngredienteLabels = {
  ingrediente_principal: "Ingrediente Principal",
  cobertura: "Cobertura",
  recheio: "Recheio",
  calda: "Calda",
  decoracao: "Decoração",
  outro: "Outro",
};

export function ReceitaViewDialog({ open, onOpenChange, receita, onEdit }: ReceitaViewDialogProps) {
  const [ingredientCosts, setIngredientCosts] = useState<Record<string, any>>({});
  const [isLoadingCosts, setIsLoadingCosts] = useState(false);

  useEffect(() => {
    if (receita && open) {
      const fetchIngredientCosts = async () => {
        setIsLoadingCosts(true);
        try {
          const produtoIds = receita.ingredientes.map(i => i.produto_id);
          if (produtoIds.length === 0) return;

          const { data } = await supabase
            .from('vw_produto_custo_unitario')
            .select('produto_id, custo_por_grama, custo_por_ml, custo_por_unidade, custo_ultimo_por_grama, custo_ultimo_por_ml, custo_ultimo_por_unidade')
            .in('produto_id', produtoIds);

          const costsMap: Record<string, any> = {};
          data?.forEach(item => {
            costsMap[item.produto_id] = item;
          });
          setIngredientCosts(costsMap);
        } catch (error) {
          console.error("Erro ao buscar custos dos ingredientes:", error);
        } finally {
          setIsLoadingCosts(false);
        }
      };
      fetchIngredientCosts();
    }
  }, [receita, open]);

  if (!receita) return null;

  const { receita: r, ingredientes } = receita;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Visualizar Receita</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Informações Básicas */}
          <div>
            <h3 className="text-lg font-semibold mb-3">Informações Básicas</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-muted-foreground">Nome</label>
                <p className="text-sm">{r.nome}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Categoria</label>
                <p className="text-sm">{r.categoria || "—"}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Subcategoria</label>
                <p className="text-sm">{r.subcategoria || "—"}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Dificuldade</label>
                <p className="text-sm">{r.dificuldade ? dificuldadeLabels[r.dificuldade] : "—"}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Porção</label>
                <p className="text-sm">{r.porcao || "—"}</p>
              </div>
            </div>
          </div>

          <Separator />

          {/* Tempos */}
          <div>
            <h3 className="text-lg font-semibold mb-3">Tempos</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-muted-foreground">Preparo</label>
                <p className="text-sm">{r.tempo_preparo_minutos ? `${r.tempo_preparo_minutos} min` : "—"}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Forno</label>
                <p className="text-sm">{r.tempo_forno_minutos ? `${r.tempo_forno_minutos} min` : "—"}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Temperatura</label>
                <p className="text-sm">{r.temperatura_forno_celsius ? `${r.temperatura_forno_celsius}°C` : "—"}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Descanso</label>
                <p className="text-sm">{r.tempo_descanso_minutos ? `${r.tempo_descanso_minutos} min` : "—"}</p>
              </div>
            </div>
          </div>

          <Separator />

          {/* Ingredientes */}
          <div>
            <h3 className="text-lg font-semibold mb-3">Ingredientes ({ingredientes.length})</h3>
            <div className="space-y-3">
              {ingredientes.map((ing) => (
                <div key={ing.id_receita_ingrediente} className="p-4 border rounded-lg bg-card">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Produto</label>
                      <p className="text-sm font-medium">
                        {String(ing.produto_nome || 'Produto não informado').replace(/0+$/, '').trim() || 'Produto não informado'}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Quantidade</label>
                      <p className="text-sm">
                        {ing.peso_gramas > 0 && `${ing.peso_gramas} g`}
                        {ing.volume_ml > 0 && `${ing.volume_ml} ml`}
                        {ing.quantidade_unidades > 0 && `${ing.quantidade_unidades} un.`}
                        {!ing.peso_gramas && !ing.volume_ml && !ing.quantidade_unidades && '—'}
                      </p>
                    </div>
                    {ing.desperdicio_percentual !== undefined && ing.desperdicio_percentual > 0 && (
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">Desperdício</label>
                        <p className="text-sm">{ing.desperdicio_percentual}%</p>
                      </div>
                    )}
                    {ing.tipo_ingrediente && (
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">Tipo</label>
                        <p className="text-sm">{ing.tipo_ingrediente}</p>
                      </div>
                    )}
                    {ing.ordem_preparo && (
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">Ordem de Preparo</label>
                        <p className="text-sm">{ing.ordem_preparo}</p>
                      </div>
                    )}
                    {ing.alergenos && (
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">Alérgenos</label>
                        <p className="text-sm text-destructive">{ing.alergenos}</p>
                      </div>
                    )}
                    {ing.substituivel && (
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">Substituível</label>
                        <p className="text-sm">{ing.substituivel}</p>
                      </div>
                    )}
                    {ing.opcional_receita && (
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">Opcional</label>
                        <p className="text-sm">{ing.opcional_receita}</p>
                      </div>
                    )}
                    {ing.observacoes && (
                      <div className="md:col-span-2">
                        <label className="text-sm font-medium text-muted-foreground">Observações</label>
                        <p className="text-sm">{ing.observacoes}</p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Modo de Preparo */}
          {r.modo_preparo && (
            <>
              <Separator />
              <div>
                <h3 className="text-lg font-semibold mb-3">Modo de Preparo</h3>
                <p className="text-sm whitespace-pre-wrap">{r.modo_preparo}</p>
              </div>
            </>
          )}

          {/* Observações */}
          {(r.observacoes_internas || r.observacoes_entrega || r.modo_armazenamento) && (
            <>
              <Separator />
              <div>
                <h3 className="text-lg font-semibold mb-3">Observações</h3>
                <div className="space-y-2">
                  {r.modo_armazenamento && (
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Armazenamento</label>
                      <p className="text-sm">{r.modo_armazenamento}</p>
                    </div>
                  )}
                  {r.validade_dias && (
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Validade</label>
                      <p className="text-sm">{r.validade_dias} dias</p>
                    </div>
                  )}
                  {r.observacoes_entrega && (
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Observações de Entrega</label>
                      <p className="text-sm">{r.observacoes_entrega}</p>
                    </div>
                  )}
                  {r.observacoes_internas && (
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Observações Internas</label>
                      <p className="text-sm">{r.observacoes_internas}</p>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          <Separator />

          {/* Custos Detalhados dos Ingredientes */}
          <div className="space-y-3">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <DollarSign className="w-5 h-5" />
              Custos Detalhados dos Ingredientes
            </h3>

            {isLoadingCosts ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Ingrediente</TableHead>
                        <TableHead className="text-right">Quantidade</TableHead>
                        <TableHead className="text-right">Preço 30d</TableHead>
                        <TableHead className="text-right">Preço Último</TableHead>
                        <TableHead className="text-right">Total (30d)</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {ingredientes.map((ing) => {
                        const costs = ingredientCosts[ing.produto_id] || {};
                        
                        let quantidade = 0;
                        let custoUnitario30d = 0;
                        let custoUnitarioUltimo = 0;
                        let unidade = '';

                        if (ing.peso_gramas) {
                          quantidade = ing.peso_gramas;
                          custoUnitario30d = costs.custo_por_grama || 0;
                          custoUnitarioUltimo = costs.custo_ultimo_por_grama || 0;
                          unidade = 'g';
                        } else if (ing.volume_ml) {
                          quantidade = ing.volume_ml;
                          custoUnitario30d = costs.custo_por_ml || 0;
                          custoUnitarioUltimo = costs.custo_ultimo_por_ml || 0;
                          unidade = 'ml';
                        } else if (ing.quantidade_unidades) {
                          quantidade = ing.quantidade_unidades;
                          custoUnitario30d = costs.custo_por_unidade || 0;
                          custoUnitarioUltimo = costs.custo_ultimo_por_unidade || 0;
                          unidade = 'un';
                        }

                        const fatorDesperdicio = 1 + ((ing.desperdicio_percentual || 0) / 100);
                        const total30d = quantidade * custoUnitario30d * fatorDesperdicio;

                        return (
                          <TableRow key={ing.id_receita_ingrediente}>
                            <TableCell className="font-medium">
                              {String(ing.produto_nome || 'Produto não informado').replace(/0+$/, '').trim() || 'Produto não informado'}
                              {ing.desperdicio_percentual && ing.desperdicio_percentual > 0 && (
                                <span className="text-xs text-muted-foreground ml-1">
                                  (+{ing.desperdicio_percentual}% desp.)
                                </span>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              {quantidade.toFixed(0)} {unidade}
                            </TableCell>
                            <TableCell className="text-right">
                              R$ {custoUnitario30d.toFixed(4)}
                            </TableCell>
                            <TableCell className="text-right">
                              R$ {custoUnitarioUltimo.toFixed(4)}
                            </TableCell>
                            <TableCell className="text-right font-semibold">
                              R$ {total30d.toFixed(2)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      <TableRow className="bg-muted/50 font-bold border-t-2">
                        <TableCell colSpan={4} className="text-right uppercase">
                          CUSTO TOTAL DA RECEITA:
                        </TableCell>
                        <TableCell className="text-right text-lg">
                          R$ {ingredientes.reduce((acc, ing) => {
                            const costs = ingredientCosts[ing.produto_id] || {};
                            let quantidade = 0;
                            let custoUnitario30d = 0;

                            if (ing.peso_gramas) {
                              quantidade = ing.peso_gramas;
                              custoUnitario30d = costs.custo_por_grama || 0;
                            } else if (ing.volume_ml) {
                              quantidade = ing.volume_ml;
                              custoUnitario30d = costs.custo_por_ml || 0;
                            } else if (ing.quantidade_unidades) {
                              quantidade = ing.quantidade_unidades;
                              custoUnitario30d = costs.custo_por_unidade || 0;
                            }

                            const fatorDesperdicio = 1 + ((ing.desperdicio_percentual || 0) / 100);
                            return acc + (quantidade * custoUnitario30d * fatorDesperdicio);
                          }, 0).toFixed(2)}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>

                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription className="text-xs">
                    💡 <strong>"Preço 30d"</strong> usa a média dos últimos 30 dias. <strong>"Preço Último"</strong> usa o custo mais recente adicionado. O total exibido considera a média 30d.
                  </AlertDescription>
                </Alert>
              </>
            )}
          </div>

          <Separator />

          {/* Metadados */}
          <div className="text-sm text-muted-foreground">
            <div>
              <span>Criada em: {format(new Date(r.created_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}</span>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Fechar
          </Button>
          <PermissionGate resource="receitas" action="edit">
            <Button onClick={onEdit}>
              <Pencil className="mr-2 h-4 w-4" />
              Editar
            </Button>
          </PermissionGate>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
